<template>
  <div>
    <!-- Reflow Dependencies - Force this component's update hook to fire when these values change -->
    <!-- This is needed for smooth reflow. Otherwise, only BaseModal's update() hook will fire -->
    <span class="hidden reflow-depencencies">
      {{ visibleAccountStatuses }}
    </span>
    <BaseModal no-exit no-padding no-toolbar card-width="550px" @close="$emit('close')">
      <template #default>
        <div ref="modalContent" class="relative w-full flex flex-col">
          <!-- Header -->
          <div class="relative w-full flex gap-2 p-5 border-b border-border-normal">
            <HealthIcon class="text-icon-normal" />
            <div class="flex flex-col gap-1">
              <BaseText type="label" size="sm" class="text-text-muted">
                Data Health
              </BaseText>
              <BaseText type="body" size="sm" class="text-text-normal">
                Live view of your data pull and any discrepancies.
              </BaseText>
            </div>
            <button class="absolute top-3 right-3 p-1 transition-opacity" @click="$emit('close')">
              <TagRemoveIcon stroke="#5E6678" :width="16" :height="16" />
            </button>
          </div>
          <!-- Body -->
          <div class="w-full flex flex-col gap-5 p-5 border-b border-border-normal">
            <!-- Frequency and Fresh pull button -->
            <div class="w-full flex flex-col">
              <BaseText type="label" size="sm" class="text-text-muted">
                Frequency
              </BaseText>
              <div class="flex items-center gap-1 py-0.5 mt-2">
                <ClockIcon stroke="#5E6678" />
                <BaseText type="body" size="sm" class="text-text-normal">
                  Last updated {{ getTimeFromNow(lastRefreshedAt) }} ago
                </BaseText>
              </div>
              <!-- PULL DATA BUTTON -->
              <button class="pull-data-btn w-full flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md mt-3"
              :disabled="pullButtonDisabled" @click="$emit('refreshLensData')">
                <RefreshIcon class="refresh-icon" :class="{'pulling': lensIsRefreshing}" />
                <BaseText type="label" size="sm" class="transition-colors duration-100" :class="pullButtonDisabled ? 'text-text-disabled' : 'text-text-muted'">
                  {{ lensIsRefreshing ? 'Pulling' : 'Pull' }} Fresh Data
                </BaseText>
              </button>
              <!-- Pull Started Indicator -->
              <div v-if="lensIsRefreshing" class="data-pull-notif w-full flex flex-col justify-end">
                <div class="w-full flex flex-col gap-1 p-2 rounded-lg" style="background-color: #EEFBFF">
                  <BaseText type="label" size="sm" style="color: #003B50">
                    Your fresh data pull has started.
                  </BaseText>
                  <BaseText type="body" size="sm" style="color: #003B50">
                    All data will be refreshed in 1-2 hours. You may close this modal.
                  </BaseText>
                </div>
              </div>
              <!-- Data Health Status Indicator -->
              <BaseText type="label" size="sm" class="text-text-muted mt-5">
                Data Check
              </BaseText>
              <div class="flex items-center gap-2 mt-2">
                <BaseText type="body" size="sm" class="text-text-muted">
                  Status
                </BaseText>
                <div class="px-2 py-0.5 rounded-md" :class="statusColorClasses">
                  <BaseText type="label" size="sm" class="capitalize">
                    {{ healthStatus }}
                  </BaseText>
                </div>
              </div>
              <!-- Connection Error -->
              <div v-if="connectionError" class="w-full h-36 flex flex-col items-center justify-center mt-3">
                <BaseText type="label" size="sm" class="text-text-muted">
                  Data Source Connection Issue
                </BaseText>
                <BaseText type="body" size="sm" class="text-text-normal mt-1">
                  We're having issues pulling data from your data sources.
                </BaseText>
                <button class="secondary-btn flex items-center gap-1 px-2 py-1.5 rounded-md mt-3" @click="navigateToSettings">
                  <SettingsIcon class="text-icon-normal" />
                  <BaseText type="label" size="sm" class="text-text-muted">
                    Resolve Issues
                  </BaseText>
                </button>
              </div>
              <div v-else class="w-full flex flex-col">
                <!-- Fancy divider (UwU) -->
                <div class="w-full flex items-center gap-2 my-2 p-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4" fill="none"><circle cx="2" cy="2" r="2" fill="#DFE1E7"/></svg>
                  <div class="flex-grow h-0 border-t border-border-normal" />
                  <svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4" fill="none"><circle cx="2" cy="2" r="2" fill="#DFE1E7"/></svg>
                </div>
                <!-- Time Range -->
                <div class="w-full flex items-center justify-between">
                  <div class="flex flex-col gap-1">
                    <BaseText type="label" size="xs" class="text-text-muted">
                      Start
                    </BaseText>
                    <BaseText type="body" size="xs" class="text-text-normal">
                      {{ timeRange.start }}
                    </BaseText>
                  </div>
                  <div class="flex flex-col gap-1 items-end">
                    <BaseText type="label" size="xs" class="text-text-muted">
                      End
                    </BaseText>
                    <BaseText type="body" size="xs" class="text-text-normal">
                      {{ timeRange.end }}
                    </BaseText>
                  </div>
                </div>
                <!-- Spend Data and Discrepancy -->
                <BaseText type="label" size="sm" class="text-text-muted mt-5">
                  Spend
                </BaseText>
                <!-- Platform Toggle -->
                <div class="w-full grid grid-cols-2 items-center gap-0.5 p-0.5 rounded-lg bg-neutral-25 overflow-hidden mt-2 flex-shrink-0">
                  <button v-for="platform in platformOptions" :key="`toggle-${platform.name}`" class="platform-toggle-btn"
                  :class="{'active': platform.isActive}" @click="platform.action">
                    <component :is="platform.icon" :width="16" :height="16" />
                    <BaseText type="label" size="sm" class="px-1">
                      {{ platform.name }}
                    </BaseText>
                  </button>
                </div>
                <div class="accounts-list">
                  <div ref="accountsList" class="w-full flex flex-col gap-2 mt-2 px-0.5">
                    <div v-if="!visibleAccountStatuses.length" class="relative w-full h-28 fade-in">
                      <div class="absolute top-0 left-0 w-full h-28 flex flex-col items-center justify-center">
                        <BaseText type="label" size="sm" class="text-text-muted">
                          No {{ activePlatform }} ad accounts connected
                        </BaseText>
                        <BaseText type="body" size="sm" class="text-text-normal mt-1">
                          Go to settings to connect more accounts to this Lens brand.
                        </BaseText>
                        <button class="secondary-btn flex items-center gap-1 px-2 py-1.5 rounded-md mt-2" @click="navigateToSettings">
                          <SettingsIcon class="text-icon-normal" />
                          <BaseText type="label" size="sm" class="text-text-muted">
                            Settings
                          </BaseText>
                        </button>
                      </div>
                    </div>
                    <div v-for="status in visibleAccountStatuses" :key="`data-health-account-${status.ad_account_id}`" 
                    class="relative w-full flex items-center justify-between fade-in">
                      <div class="flex items-center justify-center gap-1 px-2 py-1 rounded-lg bg-neutral-25 min-w-0" style="width: 184px">
                        <BaseText type="label" size="sm" class="text-text-muted truncate">
                          {{ status.accountName }}
                        </BaseText>
                        <BaseText v-if="status.hasData" type="body" size="sm" class="text-text-normal">
                          {{ status.providerAmount }}
                        </BaseText>
                      </div>
                      <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <!-- No Data -->
                        <div v-if="!status.hasData" :ref="`noDataTooltip-${status.id}`"
                        class="flex items-center gap-0.5 px-2 py-1 text-text-normal transition-colors duration-100 hover:text-text-loud cursor-default"
                        @mouseenter="hoveredNoDataTooltip = status.id" @mouseleave="hoveredNoDataTooltip = null">
                          <BaseText type="label" size="xs">
                            No Data
                          </BaseText>
                          <InfoIcon :width="16" :height="16" />
                        </div>
                        <!-- Spend Difference -->
                        <div v-else class="flex items-center gap-1 px-2 py-1">
                          <BaseText type="body" size="xs" class="text-text-normal">
                            Difference
                          </BaseText>
                          <BaseText type="label" size="xs" class="text-text-muted">
                            {{ status.percentDiff }}
                          </BaseText>
                        </div>
                      </div>
                      <div class="flex items-center justify-center gap-1 px-2 py-1 rounded-lg bg-neutral-25" style="width: 184px">
                        <BaseText type="label" size="sm" class="text-text-muted">
                          Lens
                        </BaseText>
                        <BaseText v-if="status.hasData" type="body" size="sm" class="text-text-normal">
                          {{ status.localAmount }}
                        </BaseText>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Footer -->
          <div class="w-full flex justify-between items-center p-3.5">
            <BaseText type="body" size="sm" class="text-text-normal p-1.5">
              Seeing Data Issues?
            </BaseText>
            <button class="flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
            @click="showIntercom">
              <ContactSupportIcon class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-muted">
                Contact Support
              </BaseText>
            </button>
          </div>
          <!-- No Ad Account Data Tooltip -->
          <transition>
            <div v-if="hoveredNoDataTooltip" class="tooltip shadow-md" 
            :style="{ transform: `translate(${noDataTooltipPosition.x}px, ${noDataTooltipPosition.y}px)` }">
              <BaseText type="label" size="xs" class="text-white">
                No spend data available during this period. Try adjusting your date range.
              </BaseText>
            </div>
          </transition>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { reverseProviderKeyMap } from '@/utils/lens/dataSourceUtils'
import smoothReflow from 'vue-smooth-reflow'

// Icons
import HealthIcon from '../globals/Icons/LensIcons/HealthIcon.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import ClockIcon from '../globals/Icons/ClockIcon.vue'
import RefreshIcon from '../globals/Icons/LensIcons/RefreshIcon.vue'
import ContactSupportIcon from '../globals/Icons/ContactSupportIcon.vue'
import MetaPlatformIcon from '../globals/Icons/FilterIcons/Platforms/MetaPlatformIcon.vue'
import TikTokPlatformIcon from '../globals/Icons/FilterIcons/Platforms/TikTokPlatformIcon.vue'
import SettingsIcon from '../globals/Icons/SettingsIcons/SettingsIcon.vue'
import InfoIcon from '../globals/Icons/InfoSimpleIcon.vue'

export default {
  name: 'DataHealthModal',
  mixins: [smoothReflow],
  components: {
    HealthIcon,
    TagRemoveIcon,
    ClockIcon,
    RefreshIcon,
    ContactSupportIcon,
    SettingsIcon,
    InfoIcon
  },
  props: {
    accountStatuses: {
      type: Array,
      required: true
    },
    timeRange: {
      type: Object,
      required: true
    },
    lastRefreshedAt: {
      type: String,
      required: true
    },
    healthStatus: {
      type: String,
      required: true
    },
    // Synced with parent
    lensIsRefreshing: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      platformToggle: 'meta',
      hoveredNoDataTooltip: null,
      noDataTooltipPosition: {
        x: 0,
        y: 0
      }
    }
  },
  mounted () {
    // If there are no meta accounts, set the platform toggle to tiktok
    if (!this.adAccounts.some(account => account.platform === 'meta')) {
      this.platformToggle = 'tiktok'
    }
    this.$nextTick(() => {
      this.$smoothReflow({
        el: this.$refs.accountsList,
        property: 'height',
        transition: 'height 200ms ease-in-out'
      })
    })
  },
  beforeDestroy () {
    this.$unsmoothReflow({ el: this.$refs.accountsList })
  },
  watch: {
    hoveredNoDataTooltip (newVal) {
      if (newVal) this.setNoDataTooltipPosition(newVal)
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getSidebarLenses']),
    adAccounts () {
      const currentLens = this.getSidebarLenses.find(lens => lens.id === this.$route.params.lensId)
      if (!currentLens) return []
      return this.accountStatuses.map(status => {
        const account = currentLens.accounts.find(account => account.account_id === status.ad_account_id)
        const { ad_account_currency, local_amount, provider_amount, percent_diff } = status.data_accuracy
        return {
          id: status.ad_account_id,
          platform: reverseProviderKeyMap[status.provider],
          accountName: account.account_name,
          hasData: provider_amount > 0,
          localAmount: formatCurrency(local_amount, ad_account_currency),
          providerAmount: formatCurrency(provider_amount, ad_account_currency),
          percentDiff: `${percent_diff < 0.01 ? '~' : ''}${percent_diff.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
        }
      })
    },
    visibleAccountStatuses () {
      return this.adAccounts.filter(account => account.platform === this.platformToggle)
    },
    connectionError () {
      return this.healthStatus === 'connection error'
    },
    noDataAvailable () {
      return this.visibleAccountStatuses.every(account => !account.hasData)
    },
    pullButtonDisabled () {
      return this.lensIsRefreshing
    },
    statusColorClasses () {
      switch (this.healthStatus) {
        case 'ok': return 'bg-secondary-yellow-25 text-secondary-yellow-300'
        case 'problem': return 'bg-secondary-red-25 text-secondary-red-300'
        case 'healthy': return 'bg-secondary-green-25 text-secondary-green-300'
        default: return 'bg-secondary-yellow-25 text-secondary-yellow-300'
      }
    },
    platformOptions () {
      return [
        {
          name: 'Meta',
          icon: MetaPlatformIcon,
          action: () => this.platformToggle = 'meta',
          isActive: this.platformToggle === 'meta'
        },
        {
          name: 'TikTok',
          icon: TikTokPlatformIcon,
          action: () => this.platformToggle = 'tiktok',
          isActive: this.platformToggle === 'tiktok'
        }
      ]
    },
    activePlatform () {
      return this.platformOptions.find(platform => platform.isActive)?.name
    }
  },
  methods: {
    ...mapMutations('MiscModule', ['SET_SETTINGS_DRAWER_VIEW_MODE']),
    ...mapMutations('RouterModule', ['SET_CURRENT_ROUTE_ID']),
    getTimeFromNow (timestamp) {
      if (!timestamp) return 'N/A'

      const now = new Date()
      const updated = new Date(timestamp)
      const diffMs = now - updated

      // Convert to appropriate time unit
      const diffSec = Math.floor(diffMs / 1000)
      const diffMin = Math.floor(diffSec / 60)
      const diffHour = Math.floor(diffMin / 60)
      const diffDay = Math.floor(diffHour / 24)
      const diffMonth = Math.floor(diffDay / 30)
      const diffYear = Math.floor(diffMonth / 12)

      if (diffYear > 0) return `${diffYear} year${diffYear > 1 ? 's' : ''}`
      if (diffMonth > 0) return `${diffMonth} month${diffMonth > 1 ? 's' : ''}`
      if (diffDay > 0) return `${diffDay} day${diffDay > 1 ? 's' : ''}`
      if (diffHour > 0) return `${diffHour} hour${diffHour > 1 ? 's' : ''}`
      if (diffMin > 0) return `${diffMin} minute${diffMin > 1 ? 's' : ''}`
      return `${diffSec} second${diffSec > 1 ? 's' : ''}`
    },
    showIntercom () {
      window.Intercom('show')
    },
    navigateToSettings () {
      this.SET_SETTINGS_DRAWER_VIEW_MODE('lens')
      this.SET_CURRENT_ROUTE_ID(this.$route.params.lensId)
      this.$emit('close')
    },
    setNoDataTooltipPosition (statusId) {
      if (!statusId) return
      const modalContentRect = this.$refs?.modalContent?.getBoundingClientRect()
      const labelRect = this.$refs?.[`noDataTooltip-${statusId}`]?.[0]?.getBoundingClientRect()
      if (!modalContentRect || !labelRect) return
      this.noDataTooltipPosition = {
        x: labelRect.left - modalContentRect.left - 96,
        y: labelRect.top - modalContentRect.top - 46
      }
    }
  }
}

const formatCurrency = (value, currencyCode) => {
  const currencySymbol = new Intl.NumberFormat('en', { 
    style: 'currency', 
    currency: currencyCode?.length ? currencyCode : 'USD',
    currencyDisplay: 'symbol'
  }).format(0).charAt(0)
  const hasDecimals = value % 1 !== 0
  return `${currencySymbol}${value.toLocaleString(undefined, {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0
  })}`
}
</script>

<style scoped>
.pull-data-btn {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
}
.pull-data-btn:hover {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.17), 0 0 0 1px rgba(0, 56, 108, 0.12);
}
.pull-data-btn:disabled, .pull-data-btn:disabled:hover {
  box-shadow: none;
  cursor: default;
  background-color: #F6F8FA;
}

.data-pull-notif {
  height: 72px;
  animation: dataPullNotifEnter 500ms ease-in-out forwards;
}
@keyframes dataPullNotifEnter {
  0% {
    height: 0px;
    opacity: 0;
  }
  50% {
    height: 72px;
    opacity: 0;
  }
  100% {
    height: 72px;
    opacity: 1;
  }
}

.secondary-btn {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  background-color: white;
  color: #303546;
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out, color 100ms ease-in-out;
}
.secondary-btn:hover, .secondary-btn.disabled {
  box-shadow: none;
  background-color: #F6F8FA;
}

.platform-toggle-btn {
  display: flex;
  gap: 2px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 6px 12px;
  border-radius: 6px;
  color: #5E6678;
  background-color: transparent;
  box-shadow: none;
  transition: background-color 100ms ease-in-out, color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.platform-toggle-btn:not(.active):hover {
  background-color: #ECEFF3;
  color: #303546;
}
.platform-toggle-btn.active {
  background-color: white;
  color: #303546;
  box-shadow: 0 36px 48px -12px rgba(53, 57, 75, 0.20), 0 24px 32px -12px rgba(53, 57, 75, 0.24), 0 1px 2px 0 rgba(162, 172, 186, 0.16);
}

.accounts-list {
  overflow-y: auto;
  max-height: 144px;
}
.accounts-list::-webkit-scrollbar {
  width: 5px;
}
.accounts-list::-webkit-scrollbar-thumb {
  background-color: #DFE1E7;
  border-radius: 18px;
}

.tooltip {
  position: absolute;
  left: 0;
  top: 0;
  background-color: #100f1b;
  padding: 6px 10px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  width: 270px;
  z-index: 1000;
  pointer-events: none;
  text-align: center;
}

.refresh-icon {
  color: #5E6678;
  transform: scaleX(-1);
  transition: color 100ms ease-in-out;
}
.refresh-icon.pulling {
  animation: refreshIconPulling 2s linear infinite;
  color: #A4ABB8;
}
@keyframes refreshIconPulling {
  from {
    transform: rotate(0deg) scaleX(-1);
  } 
  to {
    transform: rotate(360deg) scaleX(-1);
  }
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>